import React, { Suspense } from 'react';
import WebFont from 'webfontloader';
import { registerLicense } from '@syncfusion/ej2-base';

import { useQuery } from '@apollo/client';
import { ISACTIVE, GET_USER } from './lib/apollo';

import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import { Route, Routes } from 'react-router';
import { HashRouter as Router } from 'react-router-dom';

import ErrorBoundary from './components/ErrorBoundary';
import WaitLayer from './components/WaitLayer';
import ErrorNote, { ErrorItem } from './components/ErrorNote';
import ToastNotes, { ToastItem } from './components/ToastNotes';

WebFont.load({
    google: {
        families: ['IBM Plex Sans']
    }
});

// views code split
import LoadingView from './views/LoadingView';
const HomeView = React.lazy(() => import('./views/HomeView' /* webpackChunkName: "home-view" */));
const LoginView = React.lazy(
    () => import('./views/LoginView' /* webpackChunkName: "login-view" */)
);
const BrokenView = React.lazy(
    () => import('./views/BrokenView' /* webpackChunkName: "broken-view" */)
);
const ErrorView = React.lazy(
    () => import('./views/ErrorView/index' /* webpackChunkName: "error-view" */)
);

import { getStoredUserTheme, updateUserTheme } from './lib/utils';
import { getTheme } from './theme/AppTheme';

import './colors.css';
import './App.css';
import './textHeaders.css';
import './textParagraphs.css';
import { LicenseInfo } from '@mui/x-license';

export const GeoAppContext = React.createContext({
    user: undefined as any,
    client: undefined as any
});

const App = (props: any) => {
    const [networkError, setNetworkError] = React.useState<ErrorItem[] | null>(null);
    const [frd, setFrd] = React.useState(false);
    const [grapglToast, seGrapglToast] = React.useState<ToastItem[] | null>(null);

    const license = process.env.REACT_APP_LICENCEKEY
        ? process.env.REACT_APP_LICENCEKEY
        : 'NotValid';

    registerLicense(license.trim());

    if (process.env.REACT_APP_MUI_LICENSEKEY) {
        LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENSEKEY);
    }

    const GraphglError = (graphQLErrors: any, networkError: any) => {
        if (graphQLErrors) {
            console.error('-- GRAPHQLERRORS', graphQLErrors);
            let errArray = grapglToast ? grapglToast : [];

           

            for (let e = 0; e < graphQLErrors.length; e++) {
                const oneErr = graphQLErrors[e];
                
                errArray.push({
                    time: new Date().getTime(),
                    code:
                        oneErr.extensions && oneErr.extensions.code
                            ? oneErr.extensions.code
                            : undefined,
                    message: oneErr.message ? oneErr.message : '-'
                });
            }
            if (errArray.length > 0) {
                seGrapglToast(errArray);
                setFrd(!frd);
            }
        }

        if (networkError) {
            setNetworkError([{ message: networkError.message }]);
            setFrd(!frd);
        }
    };

    props.setErrorHandler(GraphglError);

    const getUserData = useQuery(GET_USER, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true
    });
    const loggedInUser =
        getUserData && getUserData.data && 'User' in getUserData.data
            ? getUserData.data['User']
            : null;
    const isLoggingIn = ISACTIVE(getUserData);

    const refetchUser = () => {
        getUserData.refetch();
    };

    const themeOverWrite = getTheme(
        loggedInUser
            ? updateUserTheme(
                  loggedInUser.OwnOrganization?.Theme
                      ? loggedInUser.OwnOrganization.Theme
                      : undefined
              )
            : getStoredUserTheme()
    );

    return (
        <Router>
            <ThemeProvider theme={themeOverWrite}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <div className='App'>
                        {!loggedInUser && (
                            <div>
                                <ErrorBoundary onError={ErrorView}>
                                    <Suspense fallback={<LoadingView />}>
                                        <Routes>
                                            <Route
                                                path='/'
                                                element={<LoginView />}
                                            />
                                            <Route
                                                path='/register'
                                                element={<LoginView />}
                                            />
                                            <Route
                                                path='/broken'
                                                element={<BrokenView />}
                                            />
                                            <Route
                                                path='/*'
                                                element={<LoginView />}
                                            />
                                        </Routes>
                                    </Suspense>
                                </ErrorBoundary>
                            </div>
                        )}
                        {loggedInUser && (
                            <div>
                                <GeoAppContext.Provider
                                    value={{
                                        user: loggedInUser,
                                        client: props.client
                                    }}
                                >
                                    <ErrorBoundary onError={ErrorView}>
                                        <Suspense fallback={<LoadingView />}>
                                            <HomeView refetchUser={refetchUser} />
                                        </Suspense>
                                    </ErrorBoundary>
                                </GeoAppContext.Provider>
                            </div>
                        )}
                    </div>
                    {isLoggingIn && <WaitLayer />}
                    {networkError && (
                        <ErrorNote
                            error={networkError}
                            clearError={() => {
                                setNetworkError(null);
                            }}
                        />
                    )}
                    {loggedInUser && grapglToast && (
                        <ToastNotes
                            error={grapglToast}
                            clearToast={(tid: number) => {
                                if (grapglToast && grapglToast.length > tid) {
                                    grapglToast.splice(tid, 1);
                                    seGrapglToast(grapglToast);
                                    setFrd(!frd);
                                }
                            }}
                        />
                    )}
                </LocalizationProvider>
            </ThemeProvider>
        </Router>
    );
};

export default App;
